import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { History, User, Plane, Calendar, Shield, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  update_trip: 'Updated Trip',
  update_user_permissions: 'Changed User Permissions',
  add_blackout_date: 'Added Blackout Date',
  delete_blackout_date: 'Deleted Blackout Date',
  create_promo: 'Created Promo Code',
  update_promo: 'Updated Promo Code',
  delete_promo: 'Deleted Promo Code',
};

const ACTION_ICONS = {
  update_trip: Plane,
  update_user_permissions: Shield,
  add_blackout_date: Calendar,
  delete_blackout_date: Calendar,
  create_promo: History,
  update_promo: History,
  delete_promo: History,
};

const ACTION_COLORS = {
  update_trip: 'bg-blue-100 text-blue-800',
  update_user_permissions: 'bg-purple-100 text-purple-800',
  add_blackout_date: 'bg-orange-100 text-orange-800',
  delete_blackout_date: 'bg-red-100 text-red-800',
  create_promo: 'bg-green-100 text-green-800',
  update_promo: 'bg-yellow-100 text-yellow-800',
  delete_promo: 'bg-red-100 text-red-800',
};

export default function ActivityLogsTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedLog, setExpandedLog] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [filter, page]);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams({ limit, skip: page * limit });
      if (filter !== 'all') {
        params.append('entity_type', filter);
      }
      
      const res = await axios.get(`${API}/admin/activity/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/admin/activity/logs/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch activity summary:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) return null;
    
    return (
      <div className="mt-2 space-y-1 text-sm">
        {Object.entries(changes).map(([field, change]) => (
          <div key={field} className="flex items-start gap-2">
            <span className="text-muted-foreground font-medium min-w-[100px]">{field}:</span>
            <div className="flex items-center gap-2">
              <span className="text-red-600 line-through">
                {typeof change.old === 'object' ? JSON.stringify(change.old).slice(0, 50) : String(change.old || 'none')}
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="text-green-600">
                {typeof change.new === 'object' ? JSON.stringify(change.new).slice(0, 50) : String(change.new || 'none')}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activity-logs-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-primary">{summary.total_last_24h || 0}</div>
              <p className="text-sm text-muted-foreground">Actions (24h)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">
                {summary.entities_last_24h?.trip || 0}
              </div>
              <p className="text-sm text-muted-foreground">Trip Updates</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-purple-600">
                {summary.entities_last_24h?.user || 0}
              </div>
              <p className="text-sm text-muted-foreground">User Changes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm">
                <p className="font-medium">Most Active</p>
                {summary.most_active_users?.slice(0, 2).map((u, i) => (
                  <p key={i} className="text-muted-foreground truncate">
                    {u.email} ({u.actions})
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Activity Log List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Activity Log
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={(v) => { setFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activities</SelectItem>
                <SelectItem value="trip">Trip Changes</SelectItem>
                <SelectItem value="user">User Changes</SelectItem>
                <SelectItem value="blackout">Blackout Dates</SelectItem>
                <SelectItem value="promo">Promo Codes</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={() => { fetchLogs(); fetchSummary(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No activity logs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => {
                const ActionIcon = ACTION_ICONS[log.action] || History;
                const isExpanded = expandedLog === log.id;
                
                return (
                  <div 
                    key={log.id} 
                    className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${ACTION_COLORS[log.action] || 'bg-gray-100'}`}>
                          <ActionIcon className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {ACTION_LABELS[log.action] || log.action}
                            </span>
                            {log.entity_name && (
                              <span className="text-muted-foreground">
                                "{log.entity_name}"
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <User className="h-3 w-3" />
                            <span>{log.user_email}</span>
                            <Badge variant="outline" className="text-xs">
                              {log.user_role}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(log.created_at)}
                        </span>
                        {log.changes && Object.keys(log.changes).length > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                          >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded changes view */}
                    {isExpanded && log.changes && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm font-medium mb-2">Changes:</p>
                        {renderChanges(log.changes)}
                      </div>
                    )}
                    
                    {/* Details (for non-change logs like blackout dates) */}
                    {log.details && !log.changes && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        {Object.entries(log.details).map(([key, value]) => (
                          value && <span key={key} className="mr-3">{key}: {String(value)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * limit >= total}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
